import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import SEO from "../components/SEO";
import CTAButton from "../components/CTAButton";
import SectionHeading from "../components/SectionHeading";
import ServiceCard from "../components/ServiceCard";
import ProjectCard from "../components/ProjectCard";
import ProcessTimeline from "../components/ProcessTimeline";
import { Reveal, Lines } from "../components/Reveal";
import { getProjects, getServices } from "../lib/api";
import { fadeUp, viewportOnce } from "../lib/motion";
import { PROCESS_PHASES, IMAGES, COMPANY } from "../data/content";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.68, 0.92]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[640px] bg-coal overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img
          src={IMAGES.hero}
          alt="Contemporary interior with controlled natural light (placeholder image)"
          className="w-full h-full object-cover brightness-[0.82]"
          fetchpriority="high"
        />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-gradient-to-t from-coal via-coal/40 to-coal/20" />
      <div className="bubble w-[440px] h-[440px] -right-28 top-[12%] bg-white/40" />
      <div className="bubble w-[300px] h-[300px] left-[6%] bottom-[24%] bg-mist/60" style={{ animationDelay: "-7s" }} />
      <div className="absolute top-24 right-5 sm:right-8 lg:right-12 text-right">
        <Reveal i={4}>
          <p className="label-tech text-paper/60" data-testid="hero-location-label">Working internationally</p>
        </Reveal>
      </div>
      <div className="absolute inset-x-0 bottom-0 pb-12 sm:pb-16">
        <div className="container-ax">
          <Reveal>
            <p className="label-tech text-paper/60 mb-8 flex items-center gap-3">
              <span className="inline-block w-2 h-2 rounded-full bg-electric" />
              Interior Design · Renovation · Adaptation
            </p>
          </Reveal>
          <h1 className="t-huge text-paper max-w-6xl" data-testid="hero-headline">
            <Lines lines={["From concept", "to completion."]} animateOnMount />
          </h1>
          <div className="mt-10">
            <Reveal i={3}>
              <div className="glass-dark rounded-[28px] p-6 sm:p-8 max-w-4xl flex flex-col md:flex-row md:items-center gap-6 floaty">
                <p className="text-paper/85 text-base sm:text-lg leading-relaxed md:flex-[1.4]">
                  Interior design, renovation and adaptation of residential and commercial spaces — managed from first idea to final handover.
                </p>
                <div className="flex flex-wrap gap-3 shrink-0">
                  <CTAButton to="/contact" variant="light" testId="hero-cta-primary">Discuss Your Project</CTAButton>
                  <CTAButton to="/projects" variant="outline-light" testId="hero-cta-secondary">Explore Our Work</CTAButton>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
      <motion.div
        className="absolute bottom-12 right-5 sm:right-8 lg:right-12 hidden lg:block"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
      >
        <ArrowDown size={20} className="text-paper/40" />
      </motion.div>
    </section>
  );
};

const FeaturedServiceCard = ({ service }) => (
  <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportOnce} className="h-full">
    <Link
      to={`/expertise#${service.slug}`}
      data-testid={`service-card-${service.slug}`}
      className="group relative flex flex-col justify-end h-full min-h-[440px] overflow-hidden rounded-[28px]"
    >
      <img
        src={service.image}
        alt={`${service.title} — reference image (placeholder)`}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-expo group-hover:scale-[1.05]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-coal/90 via-coal/35 to-coal/10" />
      <div className="relative p-7 sm:p-9">
        <span className="label-tech text-electric">{service.number}</span>
        <h3 className="t-h2 text-paper mt-3">{service.title}</h3>
        <p className="text-paper/70 text-sm leading-relaxed max-w-md mt-3">{service.description}</p>
        <span className="inline-flex items-center gap-2 label-tech text-paper mt-6 group-hover:text-electric transition-colors duration-300">
          Explore <ArrowUpRight size={14} />
        </span>
      </div>
    </Link>
  </motion.div>
);

const Expertise = ({ services }) => (
  <section data-testid="expertise-section" className="section-pad bg-paper border-t border-ink/10">
    <div className="container-ax">
      <SectionHeading number="01" label="Expertise" title="What we do" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {services[0] && (
          <div className="sm:col-span-2 lg:row-span-2">
            <FeaturedServiceCard service={services[0]} />
          </div>
        )}
        {services.slice(1).map((s, i) => (
          <ServiceCard key={s.slug} service={s} i={i + 1} />
        ))}
      </div>
    </div>
  </section>
);

const SelectedProjects = ({ projects }) => {
  const spans = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7"];
  return (
    <section data-testid="selected-projects" className="section-pad bg-paper border-t border-ink/10">
      <div className="container-ax">
        <div className="flex items-end justify-between gap-6 mb-14 sm:mb-20">
          <div>
            <Reveal>
              <p className="label-tech flex items-center gap-3 mb-6 text-graphite">
                <span className="text-cobalt">02</span>
                <span className="inline-block w-8 h-px bg-ink/30" />
                Explorations
              </p>
            </Reveal>
            <Reveal i={1}>
              <h2 className="t-h2 text-coal">Concepts & ideas.</h2>
            </Reveal>
          </div>
          <Reveal i={2} className="hidden md:block shrink-0">
            <CTAButton to="/projects" variant="outline-dark" testId="projects-view-all">All Projects</CTAButton>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-x-8 gap-y-16">
          {projects.slice(0, 4).map((p, i) => (
            <div key={p.slug} className={`md:col-span-1 ${spans[i % 4]} ${i % 4 === 1 ? "lg:mt-24" : ""} ${i % 4 === 3 ? "lg:-mt-10" : ""}`}>
              <ProjectCard project={p} i={i} large={i % 4 === 0 || i % 4 === 3} />
            </div>
          ))}
        </div>
        <p className="mt-14 text-xs text-graphite/70" data-testid="placeholder-note">
          Design studies, renders and ideas we are exploring — no completed works are claimed here.
        </p>
      </div>
    </section>
  );
};

const Process = () => (
  <section data-testid="process-section" className="py-16 sm:py-20 lg:py-24 bg-paper border-t border-ink/10">
    <div className="container-ax">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <Reveal>
          <p className="label-tech flex items-center gap-3 text-graphite">
            <span className="text-cobalt">03</span>
            <span className="inline-block w-8 h-px bg-ink/30" />
            How we work
          </p>
        </Reveal>
        <Reveal i={1}>
          <p className="text-graphite text-sm">One coordinated framework from first decision to final handover.</p>
        </Reveal>
      </div>
      <div className="glass rounded-3xl px-6 md:px-8">
        <ProcessTimeline phases={PROCESS_PHASES} slim />
      </div>
    </div>
  </section>
);

export const FinalCTA = () => (
  <section data-testid="final-cta" className="relative section-pad bg-gradient-to-br from-cobalt to-electric text-white overflow-hidden">
    <div className="bubble w-[480px] h-[480px] right-[-120px] top-[-120px] bg-white/25" />
    <div className="bubble w-[320px] h-[320px] left-[-80px] bottom-[-100px] bg-white/15" style={{ animationDelay: "-8s" }} />
    <div className="container-ax relative">
      <Reveal>
        <p className="label-tech text-white/70 mb-8 flex items-center gap-3">
          <span className="inline-block w-2 h-2 rounded-full bg-white" />
          Start a conversation
        </p>
      </Reveal>
      <h2 className="t-h1 max-w-4xl" data-testid="final-cta-headline">
        <Lines lines={["Planning a project?"]} />
      </h2>
      <Reveal i={2} className="mt-8 max-w-lg">
        <p className="text-white/80 text-base sm:text-lg leading-relaxed">
          Tell us what you are building, where the project is located and which services you require.
        </p>
      </Reveal>
      <Reveal i={3} className="mt-10">
        <CTAButton to="/contact" variant="light" testId="final-cta-button">Discuss Your Project</CTAButton>
      </Reveal>
      <Reveal i={4} className="mt-16 pt-8 border-t border-white/25 flex flex-wrap gap-x-12 gap-y-3 text-sm text-white/80">
        <a href={`mailto:${COMPANY.email}`} data-testid="final-cta-email" className="hover:text-white transition-colors">{COMPANY.email}</a>
      </Reveal>
    </div>
  </section>
);

const Home = () => {
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    getServices().then(setServices).catch(() => {});
    getProjects({ featured: true })
      .then((d) =>
        setProjects(
          [...d].sort((a, b) => {
            const pri = (p) => (p.type === "Renovation" || p.type === "Interior" ? 0 : 1);
            return pri(a) - pri(b);
          })
        )
      )
      .catch(() => {});
  }, []);

  return (
    <>
      <SEO
        title="ARCH EXPO"
        description="ARCH EXPO — integrated architecture, interior design, construction, procurement and project management services from concept to completion."
      />
      <Hero />
      <Expertise services={services} />
      <SelectedProjects projects={projects} />
      <Process />
      <FinalCTA />
    </>
  );
};

export default Home;
